import classNames from "classnames";
import React, { useEffect, useRef } from "react";
import SVG from "react-inlinesvg";
import { CSSTransition } from "react-transition-group";
import { Key } from "ts-key-enum";
import { c, t } from "ttag";

import iconXClose from "../../../../img/icons/x-close.svg";
import { ISafeHTML, isoSafeHTML } from "../../../models/nominals";
import { ICompareTile } from "../../../models/product-compare.interfaces";
import { focusElement, trapFocus } from "../../../utils/keyboardFocus";
import { ProductCompareGrid__ColumnCard_Component } from "../../product-compare/components/ProductCompareGrid__ColumnCard";
import { useAppSelector } from "../../reducers";
import { MOBILE_WIDTH_THRESHOLD_ALTERNATIVE } from "../constants";
import { IModularConfiguratorOptionSet } from "../models.interfaces";
import { showFinanceBlock } from "./ConfiguratorPanelTrigger";

import styles from "./RootProductCompareDrawer.module.scss";

interface IOwnProps {
    optionSet: IModularConfiguratorOptionSet;
    prefixNameCapped: string;
    panelClass: string;
    isPanelOpen: boolean;
    onCloseButtonClick: (
        removeStaticFromBody: () => void,
        removePanelStateToBody: () => void,
        showFinanceBlock: () => void,
    ) => void;
}

type IProps = IOwnProps;

export const RootProductCompareDrawer = (props: IProps) => {
    const optionPanel = useRef<HTMLDivElement | null>(null);
    const closeButton = useRef<HTMLButtonElement | null>(null);

    const rootProducts = useAppSelector(
        (state) => state.configurator.entities.rootProducts,
    );
    const viewport = useAppSelector((state) => state.common.viewport);
    const priceSelectValue = useAppSelector(
        (state) => state.productcompare.priceSelectValue,
    );

    const onEscKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === Key.Escape) {
            props.onCloseButtonClick(
                removeStaticFromBody,
                removePanelStateToBody,
                showFinanceBlock,
            );
        }
    };

    const isMobileWidth = () => {
        return window.innerWidth < MOBILE_WIDTH_THRESHOLD_ALTERNATIVE;
    };

    const addStaticToBody = () => {
        document.body.classList.add("static");
    };

    const removeStaticFromBody = () => {
        document.body.classList.remove("static");
    };

    const addPanelStateToBody = () => {
        document.body.classList.add("configurator-panel-open");
    };

    const removePanelStateToBody = () => {
        document.body.classList.remove("configurator-panel-open");
    };

    const onMouseEnterPanel = () => {
        if (optionPanel) {
            addStaticToBody();
        }
    };

    const onMouseLeavePanel = () => {
        if (optionPanel) {
            removeStaticFromBody();
        }
    };

    const getTiles = (): ICompareTile[] => {
        if (!props.optionSet.root_product_compare_modal) {
            return [];
        }
        const baseTiles = props.optionSet.root_product_compare_modal.products;
        // Annotate each tile with the related product data
        const tiles = baseTiles
            .map((baseTile): ICompareTile => {
                const rootProduct = rootProducts.find((rp) => {
                    return rp.id === baseTile.product_compare_tile.product_id;
                });
                return {
                    ...baseTile,
                    rootProducts: rootProduct ? [rootProduct] : [],
                };
            })
            .filter((tile) => tile.rootProducts.length > 0);
        return tiles;
    };

    const buildHeader = (): ISafeHTML => {
        if (!props.optionSet.root_product_compare_modal_title) {
            return isoSafeHTML.wrap("");
        }

        return isoSafeHTML.wrap(
            `<h3>${props.optionSet.root_product_compare_modal_title}</h3>`,
        );
    };

    useEffect(() => {
        // Wait for animation completed
        const timeoutRef = setTimeout(() => {
            if (closeButton?.current) {
                focusElement(closeButton.current);
            }
            if (optionPanel?.current) {
                trapFocus(optionPanel.current);
            }
        }, 500);

        if (isMobileWidth()) {
            addStaticToBody();
        }

        addPanelStateToBody();

        return () => {
            removeStaticFromBody();
            removePanelStateToBody();
            clearTimeout(timeoutRef);
        };
    }, [
        focusElement,
        trapFocus,
        isMobileWidth,
        addStaticToBody,
        addPanelStateToBody,
        removeStaticFromBody,
        removePanelStateToBody,
    ]);

    if (!props.optionSet.root_product_compare_modal) {
        return null;
    }
    const panelClasses = classNames({
        configurator__panel: true,
        [styles.panel]: true,
        [`configurator__panel--${props.panelClass}`]: true,
        [`configurator__panel--compare-drawer`]: true,
    });
    const tiles = getTiles();
    return (
        <>
            <CSSTransition
                in={props.isPanelOpen}
                timeout={500}
                classNames="configurator__panel-"
                unmountOnExit={true}
                nodeRef={optionPanel}
            >
                <div
                    aria-modal="true"
                    aria-label={c("RootProductCompareDrawer")
                        .t`${props.prefixNameCapped} option selection panel`}
                    className={panelClasses}
                    ref={optionPanel}
                    role="dialog"
                    onKeyDown={onEscKeyDown}
                    onMouseEnter={onMouseEnterPanel}
                    onMouseLeave={onMouseLeavePanel}
                >
                    <div className={styles.root}>
                        <div
                            className={`${styles.head} configurator__panel--head`}
                        >
                            {" "}
                            <h5>Compare</h5>
                            <button
                                ref={closeButton}
                                aria-label={t`Close`}
                                onClick={() =>
                                    props.onCloseButtonClick(
                                        removePanelStateToBody,
                                        removePanelStateToBody,
                                        showFinanceBlock,
                                    )
                                }
                            >
                                <SVG aria-hidden="true" src={iconXClose} />
                            </button>
                        </div>
                        {/* wrapping with the same class as the Wagtail Block Variant to ensure proper styling */}
                        <div
                            className={`${styles.grid} product-compare-grid-column-card`}
                        >
                            <ProductCompareGrid__ColumnCard_Component
                                header={buildHeader()}
                                tiles={tiles}
                                priceSelectValue={priceSelectValue}
                                viewport={viewport}
                            />
                        </div>
                    </div>
                </div>
            </CSSTransition>
        </>
    );
};
